/**
 * Call Centre ("Call campaigns") helpers: paths, React Query keys, friendly error messages (the API's error
 * codes arrive in errors[0]), times of day (HHmm ints), calling days (bitmask) and small text helpers.
 *
 * Stamps (yyyyMMddHHmmss numbers already localised to the viewer), yyyy-MM-dd dates, CSV downloads and plurals
 * reuse the Procedures helpers so both consoles format things the same way.
 *
 * All copy here is South African English with no long dashes. Members are never messaged by this module.
 */

import {
  CALL_CENTRE_CAMPAIGN_STATUS,
  CALL_CENTRE_CONTACT_STATUS,
  CALL_CENTRE_ERROR_CODES,
  CALL_CENTRE_IMPORT_MATCH_STATUS,
  CALL_CENTRE_IMPORT_BATCH_STATUS,
  CALL_CENTRE_QUESTION_TYPE,
  CALL_CENTRE_DETAIL_CHANGE_MODE,
  CALL_CENTRE_ASSIGNMENT_STRATEGY,
  CALL_CENTRE_JOURNEY_STEP_STATUS,
  CALL_CENTRE_HANDOFF_STATUS,
  CALL_CENTRE_PCO_LINK_STATUS,
  CALL_CENTRE_OUTCOME_GROUP,
  CALL_CENTRE_LIMITS,
  CALL_CENTRE_CAMPAIGN_TYPE,
  CALL_CENTRE_LIST_SOURCE_TYPE,
  CALL_CENTRE_SYNC_STATUS,
  CALL_CENTRE_SYNC_JOB_TYPE,
  CALL_CENTRE_DETAIL_CHANGE_STATUS,
  CALL_CENTRE_JOURNEY_BOOKING_TARGET_TYPE,
  CALL_CENTRE_JOURNEY_PCO_SYNC_MODE,
  type CallCentreCampaignView,
  type CallCentreCampaignSaveRequest,
  type CallCentreSyncJobView,
} from '@actschurch/ccms-sdk';

export {
  CALL_CENTRE_CAMPAIGN_STATUS,
  CALL_CENTRE_CONTACT_STATUS,
  CALL_CENTRE_ERROR_CODES,
  CALL_CENTRE_IMPORT_MATCH_STATUS,
  CALL_CENTRE_IMPORT_BATCH_STATUS,
  CALL_CENTRE_QUESTION_TYPE,
  CALL_CENTRE_DETAIL_CHANGE_MODE,
  CALL_CENTRE_ASSIGNMENT_STRATEGY,
  CALL_CENTRE_JOURNEY_STEP_STATUS,
  CALL_CENTRE_HANDOFF_STATUS,
  CALL_CENTRE_PCO_LINK_STATUS,
  CALL_CENTRE_OUTCOME_GROUP,
  CALL_CENTRE_LIMITS,
  CALL_CENTRE_CAMPAIGN_TYPE,
  CALL_CENTRE_LIST_SOURCE_TYPE,
  CALL_CENTRE_SYNC_STATUS,
  CALL_CENTRE_SYNC_JOB_TYPE,
  CALL_CENTRE_DETAIL_CHANGE_STATUS,
  CALL_CENTRE_JOURNEY_BOOKING_TARGET_TYPE,
  CALL_CENTRE_JOURNEY_PCO_SYNC_MODE,
};

// Same stamp, date, file and plural helpers as the Procedures console.
export {
  stampToDate,
  formatStampDate,
  formatStampDateTime,
  formatStampTime,
  relativeStamp,
  dateToYmd,
  ymdToDate,
  formatYmd,
  formatDuration,
  downloadBlob,
  plural,
  colorFor,
  initials,
} from '../../ProceduresManagement/components/proceduresUtils';

// ---------------------------------------------------------------- paths

export const CALLCENTRE_BASE_PATH = '/app/callcentre';

export const callCentrePaths = {
  dashboard: CALLCENTRE_BASE_PATH,
  campaigns: `${CALLCENTRE_BASE_PATH}/campaigns`,
  newCampaign: `${CALLCENTRE_BASE_PATH}/campaigns/new`,
  campaign: (id: number | string, tab?: string) => `${CALLCENTRE_BASE_PATH}/campaigns/${id}${tab && tab !== 'overview' ? `?tab=${tab}` : ''}`,
  setup: (id: number | string, step?: number) => `${CALLCENTRE_BASE_PATH}/campaigns/${id}/setup${step != null ? `?step=${step}` : ''}`,
  /** The full-screen conversation guide builder (`ai` opens Build with AI straight away). */
  campaignGuide: (id: number | string, ai?: boolean) => `${CALLCENTRE_BASE_PATH}/campaigns/${id}/guide${ai ? '?ai=build' : ''}`,
  /** The builder for a campaign type's starter guide. */
  starterGuide: (typeId: number | string) => `${CALLCENTRE_BASE_PATH}/settings/campaign-types/${typeId}/guide`,
  importReview: (batchId: number | string) => `${CALLCENTRE_BASE_PATH}/imports/${batchId}`,
  contacts: `${CALLCENTRE_BASE_PATH}/contacts`,
  contact: (id: number | string) => `${CALLCENTRE_BASE_PATH}/contacts/${id}`,
  doNotCall: `${CALLCENTRE_BASE_PATH}/do-not-call`,
  detailChanges: `${CALLCENTRE_BASE_PATH}/detail-changes`,
  journey: (tab?: string) => `${CALLCENTRE_BASE_PATH}/journey${tab && tab !== 'board' ? `?tab=${tab}` : ''}`,
  handoffs: (tab?: string) => `${CALLCENTRE_BASE_PATH}/handoffs${tab && tab !== 'handoffs' ? `?tab=${tab}` : ''}`,
  callers: `${CALLCENTRE_BASE_PATH}/callers`,
  reports: `${CALLCENTRE_BASE_PATH}/reports`,
  settings: (tab?: string) => `${CALLCENTRE_BASE_PATH}/settings${tab && tab !== 'general' ? `?tab=${tab}` : ''}`,
  telephony: (tab?: string) => `${CALLCENTRE_BASE_PATH}/telephony${tab && tab !== 'connection' ? `?tab=${tab}` : ''}`,
  stations: `${CALLCENTRE_BASE_PATH}/telephony?tab=stations`,
  member: (memberId: number) => `/admin/members/${memberId}`,
};

// ---------------------------------------------------------------- query keys

export const callCentreKeys = {
  all: ['callcentre'] as const,
  lookups: () => ['callcentre', 'lookups'] as const,
  ministries: () => ['callcentre', 'ministries'] as const,
  settings: () => ['callcentre', 'settings'] as const,
  // 271j: member fields callers may add when missing / update
  memberFields: () => ['callcentre', 'settings', 'member-fields'] as const,
  dashboard: () => ['callcentre', 'dashboard'] as const,
  reminders: (includeRead: boolean) => ['callcentre', 'reminders', includeRead] as const,
  remindersRoot: () => ['callcentre', 'reminders'] as const,
  campaignsRoot: () => ['callcentre', 'campaigns'] as const,
  campaigns: (q: unknown) => ['callcentre', 'campaigns', q] as const,
  campaign: (id: number) => ['callcentre', 'campaign', id] as const,
  // 271m: call results per campaign, the messages to the people called and the message library
  campaignResults: (id: number) => ['callcentre', 'campaign-results', id] as const,
  messagePresets: () => ['callcentre', 'message-presets'] as const,
  contactMessages: (id: number) => ['callcentre', 'contact-messages', id] as const,
  // 271i: conversation guides
  campaignGuide: (id: number) => ['callcentre', 'guide', 'campaign', id] as const,
  campaignGuideReport: (id: number, version: unknown) => ['callcentre', 'guide', 'report', id, version] as const,
  campaignGuidePaths: (id: number, q: unknown) => ['callcentre', 'guide', 'paths', id, q] as const,
  typeGuide: (typeId: number) => ['callcentre', 'guide', 'type', typeId] as const,
  // 271k: the member custom fields guide rules may look at
  guideFactOptions: () => ['callcentre', 'guide', 'fact-options'] as const,
  workload: (id: number) => ['callcentre', 'workload', id] as const,
  campaignImports: (id: number) => ['callcentre', 'campaign-imports', id] as const,
  contactsRoot: () => ['callcentre', 'contacts'] as const,
  contacts: (scope: unknown, q: unknown) => ['callcentre', 'contacts', scope, q] as const,
  contact: (id: number) => ['callcentre', 'contact', id] as const,
  importBatch: (id: number) => ['callcentre', 'import', id] as const,
  importRowsRoot: (id: number) => ['callcentre', 'import-rows', id] as const,
  importRows: (id: number, q: unknown) => ['callcentre', 'import-rows', id, q] as const,
  doNotCallRoot: () => ['callcentre', 'do-not-call'] as const,
  doNotCall: (q: unknown) => ['callcentre', 'do-not-call', q] as const,
  detailChangesRoot: () => ['callcentre', 'detail-changes'] as const,
  detailChanges: (q: unknown) => ['callcentre', 'detail-changes', q] as const,
  journeyBoard: () => ['callcentre', 'journey', 'board'] as const,
  journeySteps: () => ['callcentre', 'journey', 'steps'] as const,
  memberJourney: (memberId: number) => ['callcentre', 'journey', 'member', memberId] as const,
  handoffsRoot: () => ['callcentre', 'handoffs'] as const,
  handoffs: (q: unknown) => ['callcentre', 'handoffs', q] as const,
  ministryOwners: () => ['callcentre', 'ministry-owners'] as const,
  callers: () => ['callcentre', 'callers'] as const,
  report: (q: unknown) => ['callcentre', 'report', q] as const,
  // Stage 2: list sources and Planning Centre linking
  sources: (kind: string, q: unknown) => ['callcentre', 'sources', kind, q] as const,
  syncJobsRoot: () => ['callcentre', 'sync-jobs'] as const,
  syncJobs: (q: unknown) => ['callcentre', 'sync-jobs', q] as const,
  syncJob: (id: number, q: unknown) => ['callcentre', 'sync-jobs', 'job', id, q] as const,
  pcoPick: (personId: number | null) => ['callcentre', 'pco-pick', personId] as const,
  // Stage 3: phone system
  telephony: () => ['callcentre', 'telephony'] as const,
  cdrRoot: () => ['callcentre', 'telephony', 'cdr'] as const,
  cdr: (q: unknown) => ['callcentre', 'telephony', 'cdr', q] as const,
  stations: () => ['callcentre', 'stations'] as const,
  // Stage 4: journey integrations
  courseSources: () => ['callcentre', 'sources', 'courses'] as const,
  pcoFieldDefinitions: () => ['callcentre', 'pco', 'field-definitions'] as const,
  pcoWorkflows: () => ['callcentre', 'pco', 'workflows'] as const,
  // Stage 5: notification topics, Acts Care needs (for the Care referral setting), caller teams
  notificationTopics: () => ['callcentre', 'settings', 'notification-topics'] as const,
  careNeeds: () => ['callcentre', 'care-needs'] as const,
  teams: () => ['callcentre', 'teams'] as const,
  // Stage 6: AI assist
  aiAvailability: () => ['callcentre', 'ai', 'availability'] as const,
  aiInsights: (days: number) => ['callcentre', 'ai', 'insights', days] as const,
};

// ---------------------------------------------------------------- errors

/** 409 from link-pco: nobody on the campaign needs linking (the code is not in the SDK constants). */
export const CALL_CENTRE_NOTHING_TO_LINK = 'nothing_to_link';

const ERROR_COPY: Record<string, string> = {
  [CALL_CENTRE_ERROR_CODES.NoAccess]: "You don't have access to the call centre. Ask a call centre administrator.",
  [CALL_CENTRE_ERROR_CODES.NotAssigned]: "That person isn't on this caller's list.",
  [CALL_CENTRE_ERROR_CODES.CampaignNotActive]: 'The campaign is not live. Launch it (or resume it) first.',
  [CALL_CENTRE_ERROR_CODES.CampaignNotFound]: "That campaign couldn't be found. It may have been deleted.",
  [CALL_CENTRE_ERROR_CODES.ContactNotFound]: "That person couldn't be found on the campaign. They may have been removed.",
  [CALL_CENTRE_ERROR_CODES.ContactClosed]: 'That person is already closed on this campaign. Reopen them first.',
  [CALL_CENTRE_ERROR_CODES.OutsideCallWindow]: "It's outside the calling hours set in the call centre settings.",
  [CALL_CENTRE_ERROR_CODES.DoNotCall]: 'That number is on the do not call list.',
  [CALL_CENTRE_ERROR_CODES.InvalidPhone]: "That phone number doesn't look right. Use a South African cell number or the full international number.",
  [CALL_CENTRE_ERROR_CODES.DuplicateContact]: 'That person is already on this campaign.',
  [CALL_CENTRE_ERROR_CODES.ImportNotReady]: "This list isn't ready yet. Wait for the matching to finish, then try again.",
  [CALL_CENTRE_ERROR_CODES.SelfClaimDisabled]: 'Callers may not claim people on this campaign.',
  [CALL_CENTRE_ERROR_CODES.NothingToClaim]: 'There is nobody left to hand out.',
  [CALL_CENTRE_ERROR_CODES.DetailChangesDisabled]: 'Detail changes are switched off.',
  [CALL_CENTRE_ERROR_CODES.MemberServiceUnavailable]:
    "The member service isn't answering right now, so people can't be matched to CCMS members. Wait a moment and try again.",
  [CALL_CENTRE_ERROR_CODES.StepInUse]: 'That journey step is in use, so it can only be switched off, not changed that way.',
  [CALL_CENTRE_ERROR_CODES.OutcomeInUse]: 'That outcome has been used on calls, so it can only be switched off.',
  [CALL_CENTRE_ERROR_CODES.AiUnavailable]: 'AI help is switched off.',
  [CALL_CENTRE_ERROR_CODES.AiFailed]: 'AI help is not available right now. Carry on as usual.',
  [CALL_CENTRE_ERROR_CODES.PhoneSystemUnavailable]:
    "The phone system didn't answer. Check the settings and run Diagnose; callers dial from their own phone until it works.",
  [CALL_CENTRE_NOTHING_TO_LINK]:
    "Nobody on this campaign needs linking: everyone is linked already, is waiting in another job or has no CCMS member yet (switch on Create CCMS members).",
};

/** The Call Centre error code in errors[0], if any. */
export function callCentreErrorCode(err: unknown): string | undefined {
  const e = err as { errors?: unknown } | null;
  if (e && Array.isArray(e.errors) && typeof e.errors[0] === 'string') return e.errors[0] as string;
  return undefined;
}

/** HTTP status of an SDK error (CCMSError.status), if any. */
export function callCentreErrorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; statusCode?: number } | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

/** True for the 503 the import answers when Member Management can't be reached (worth a retry). */
export function isMemberServiceUnavailable(err: unknown): boolean {
  return callCentreErrorCode(err) === CALL_CENTRE_ERROR_CODES.MemberServiceUnavailable || callCentreErrorStatus(err) === 503;
}

/**
 * Planning Centre actions (find, link, create, link a campaign): the 503 / 429 there means Member Management or
 * Planning Centre is busy, not that matching failed, so it gets its own copy.
 */
export function pcoErrorMessage(err: unknown, fallback = "Planning Centre couldn't be reached. Please try again."): string {
  if (isMemberServiceUnavailable(err) || callCentreErrorStatus(err) === 429) {
    return 'Planning Centre or the member service is busy right now. Wait a minute and try again.';
  }
  return callCentreErrorMessage(err, fallback);
}

/** A friendly message for an SDK error: known error codes first, then the API message. */
export function callCentreErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const code = callCentreErrorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  const status = callCentreErrorStatus(err);
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That item couldn't be found. It may have been deleted.";
  if (status === 503) return ERROR_COPY[CALL_CENTRE_ERROR_CODES.MemberServiceUnavailable];
  const e = err as { message?: string; errors?: unknown };
  const details = Array.isArray(e.errors)
    ? (e.errors as unknown[]).filter((x): x is string => typeof x === 'string' && !/^[a-z_]+$/.test(x))
    : [];
  const base = e.message || fallback;
  return details.length ? `${base}: ${details.join('; ')}` : base;
}

// ---------------------------------------------------------------- times of day and calling days

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 800 to "08:00", 2030 to "20:30". */
export function hhmmToTime(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const h = Math.floor(value / 100);
  const m = value % 100;
  return `${pad2(h)}:${pad2(m)}`;
}

/** "08:00" to 800. */
export function timeToHhmm(value: string | null | undefined): number | null {
  if (!value) return null;
  const [h, m] = value.split(':').map((x) => Number(x));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 100 + m;
}

/** Bit 0 = Monday ... bit 6 = Sunday. */
export const CALL_DAYS = [
  { bit: 0, short: 'Mon', label: 'Monday' },
  { bit: 1, short: 'Tue', label: 'Tuesday' },
  { bit: 2, short: 'Wed', label: 'Wednesday' },
  { bit: 3, short: 'Thu', label: 'Thursday' },
  { bit: 4, short: 'Fri', label: 'Friday' },
  { bit: 5, short: 'Sat', label: 'Saturday' },
  { bit: 6, short: 'Sun', label: 'Sunday' },
];

export function callDaysLabel(mask: number): string {
  if ((mask & 127) === 127) return 'Every day';
  if ((mask & 127) === 31) return 'Weekdays';
  const days = CALL_DAYS.filter((d) => (mask & (1 << d.bit)) !== 0).map((d) => d.short);
  return days.length ? days.join(', ') : 'No days';
}

// ---------------------------------------------------------------- text

/** "Thabo" + "Nkosi" to "Thabo Nkosi". */
export const fullName = (first?: string | null, last?: string | null) => [first, last].filter((x) => !!x && x.trim()).join(' ').trim() || 'No name';

/** "Newcomers, October" to "newcomers-october" (file names). */
export function slugifyName(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'campaign'
  );
}

/** 125 minutes to "2 h 5 min". */
export function minutesLabel(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return 'Not set';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Merge fields the caller portal fills in on the script. */
export const SCRIPT_MERGE_FIELDS = ['[first name]', '[surname]', '[your name]', '[campaign]'];

/** Mantine colour names for colour pickers on outcomes, campaign types and journey steps. */
export const COLOUR_OPTIONS = ['blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange', 'red', 'pink', 'grape', 'violet', 'indigo', 'gray', 'dark'];

/** A colour from the API as a CSS colour (charts, dots): Mantine names become theme variables, hex stays hex. */
export function cssColour(c: string | null | undefined, fallback = 'gray'): string {
  const v = (c || '').trim() || fallback;
  if (/^(#|rgb|hsl|var\()/i.test(v)) return v;
  return `var(--mantine-color-${v}-6)`;
}

/** A colour from the API (a Mantine colour name or a hex value) safe to hand to a Badge. */
export const badgeColour = (c?: string | null, fallback = 'gray') => (c && c.trim() ? c.trim() : fallback);

// ---------------------------------------------------------------- campaigns

/** Build the full save request from a loaded campaign (PUT replaces the whole campaign). */
export function campaignToSaveRequest(c: CallCentreCampaignView, patch: Partial<CallCentreCampaignSaveRequest> = {}): CallCentreCampaignSaveRequest {
  return {
    name: c.name,
    description: c.description ?? null,
    campaignTypeID: c.campaignTypeID,
    locationID: c.locationID ?? null,
    startDate: c.startDate ?? null,
    endDate: c.endDate ?? null,
    goal: c.goal ?? null,
    script: c.script ?? null,
    maxAttempts: c.maxAttempts ?? null,
    retrySpacingMinutes: c.retrySpacingMinutes ?? null,
    coolDownDays: c.coolDownDays ?? null,
    assignmentStrategyID: c.assignmentStrategyID,
    allowSelfClaim: c.allowSelfClaim,
    selfClaimBatchSize: c.selfClaimBatchSize,
    ministryID: c.ministryID ?? null,
    journeyFocusStepID: c.journeyFocusStepID ?? null,
    detailChangeModeID: c.detailChangeModeID,
    color: c.color ?? null,
    ownerUserId: c.ownerUserId ?? null,
    ...patch,
  };
}

/** The status moves the doc allows: Draft to Active, Active and Paused both ways, Active or Paused to Completed, any to Archived, Archived to Draft. */
export interface StatusMove {
  to: number;
  label: string;
  color: string;
  confirm?: string;
}

export function allowedStatusMoves(statusId: number): StatusMove[] {
  const S = CALL_CENTRE_CAMPAIGN_STATUS;
  const archive: StatusMove = { to: S.Archived, label: 'Archive', color: 'gray', confirm: 'The campaign is hidden from the lists and callers can no longer work on it. You can restore it later as a draft.' };
  switch (statusId) {
    case S.Draft:
      return [{ to: S.Active, label: 'Launch', color: 'green', confirm: 'Callers can start calling straight away. People already shared out show in their call lists.' }, archive];
    case S.Active:
      return [
        { to: S.Paused, label: 'Pause', color: 'yellow', confirm: 'Callers stop getting people from this campaign until you resume it.' },
        { to: S.Completed, label: 'Complete', color: 'blue', confirm: 'The campaign is closed for calling. People still open stay as they are for reporting.' },
        archive,
      ];
    case S.Paused:
      return [
        { to: S.Active, label: 'Resume', color: 'green' },
        { to: S.Completed, label: 'Complete', color: 'blue', confirm: 'The campaign is closed for calling. People still open stay as they are for reporting.' },
        archive,
      ];
    case S.Completed:
      return [archive];
    case S.Archived:
      return [{ to: S.Draft, label: 'Restore', color: 'blue', confirm: 'The campaign goes back to Draft. Launch it again when it is ready.' }];
    default:
      return [];
  }
}

/** A Planning Centre sync job still working: items queued or running, or the job itself queued or running. */
export function isSyncJobOpen(job: CallCentreSyncJobView): boolean {
  return job.openItems > 0 || job.statusID === CALL_CENTRE_SYNC_STATUS.Queued || job.statusID === CALL_CENTRE_SYNC_STATUS.Running;
}

/** Split an array into chunks of at most `size`. */
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// ---------------------------------------------------------------- handoff hand-overs (Stage 5)

/** ExternalRef "care:CR-..." = the referral went to Acts Care. */
export const isCareExternalRef = (ref: string | null | undefined): boolean => !!ref && ref.startsWith('care:');

/** ExternalRef as a short label: "Acts Care CR-202610-004" or "Added to the ministry" (null when there is none). */
export function handoffExternalRefLabel(ref: string | null | undefined): string | null {
  if (!ref) return null;
  if (ref.startsWith('care:')) return `Acts Care ${ref.slice('care:'.length)}`;
  if (ref.startsWith('ministry:')) return 'Added to the ministry';
  return ref;
}
